import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  BookOpenText,
  CalendarDays,
  MessagesSquare,
  Power,
  Search,
  Stethoscope,
  Trash2,
  UserRoundSearch,
  UsersRound,
} from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, PriorityBadge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Feedback";
import { DataTable, formatDate, type Column } from "@/components/ui/DataTable";
import { useToast } from "@/components/ui/toast";
import { useSession, isAdmin } from "@/lib/session";

/** Shared fetch + error shell so each list screen stays to its columns. */
function useList<T>(key: string[], path: string, schema: z.ZodType<{ items: T[] }>) {
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api.get(path, schema, signal),
  });
}

function ListError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <Alert tone="danger" title="Gagal memuat data" className="mb-5">
      {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
    </Alert>
  );
}

// --------------------------------------------------------------------------
// Patients
// --------------------------------------------------------------------------

const patientSchema = z.object({
  items: z
    .array(
      z.object({
        rm_number: z.string(),
        name: z.string(),
        email: z.string().nullable().default(null),
        phone: z.string().nullable().default(null),
        gender: z.string().nullable().default(null),
        dob: z.string().nullable().default(null),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

type Patient = z.infer<typeof patientSchema>["items"][number];

export function AdminPatients() {
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState("");
  const { data, isPending, error } = useList<Patient>(
    ["admin", "patients", query],
    `/api/admin/patients?q=${encodeURIComponent(query)}`,
    patientSchema,
  );

  const columns: Column<Patient>[] = [
    {
      key: "rm",
      header: "No. RM",
      width: "170px",
      render: (row) => <span className="tabular font-semibold">{row.rm_number}</span>,
    },
    { key: "name", header: "Nama", render: (row) => row.name },
    {
      key: "contact",
      header: "Kontak",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate">{row.phone ?? "—"}</p>
          <p className="text-content-subtle truncate text-xs">{row.email ?? "—"}</p>
        </div>
      ),
    },
    { key: "gender", header: "Gender", width: "120px", render: (row) => row.gender ?? "—" },
    {
      key: "created",
      header: "Terdaftar",
      width: "140px",
      render: (row) => (
        <span className="text-content-muted text-xs">{formatDate(row.created_at)}</span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Registrasi"
        title="Pasien"
        description="Seluruh akun Portal Pasien yang terdaftar."
      />
      <ListError error={error} />
      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(term.trim());
          }}
          className="border-hairline flex gap-2 border-b p-4"
        >
          <Input
            placeholder="Cari nama, nomor RM, atau telepon"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            className="flex-1"
          />
          <Button type="submit" icon={<Search className="size-4" />}>
            Cari
          </Button>
        </form>
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(row) => row.rm_number}
          isPending={isPending}
          empty={{
            icon: <UserRoundSearch className="size-5" />,
            title: query ? "Tidak ada hasil" : "Belum ada pasien terdaftar",
          }}
        />
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Conversations
// --------------------------------------------------------------------------

const conversationSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        ticket: z.string(),
        patient: z.string().nullable().default(null),
        topic: z.string().nullable().default(null),
        intent: z.string().nullable().default(null),
        status: z.string().nullable().default(null),
        priority: z.string().nullable().default(null),
        channel: z.string().nullable().default(null),
        created_at: z.string().nullable().default(null),
        assigned_to: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

type Conversation = z.infer<typeof conversationSchema>["items"][number];

export function AdminConversations() {
  const { data, isPending, error } = useList<Conversation>(
    ["admin", "conversations"],
    "/api/admin/conversations",
    conversationSchema,
  );

  const columns: Column<Conversation>[] = [
    {
      key: "ticket",
      header: "Tiket",
      width: "160px",
      render: (row) => <span className="tabular font-semibold">{row.ticket}</span>,
    },
    { key: "patient", header: "Pasien", render: (row) => row.patient ?? "Tamu" },
    {
      key: "topic",
      header: "Topik",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate">{row.topic ?? "—"}</p>
          <p className="text-content-subtle truncate text-xs">{row.intent ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "priority",
      header: "Prioritas",
      width: "130px",
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      render: (row) => <Badge>{row.status ?? "baru"}</Badge>,
    },
    {
      key: "created",
      header: "Dibuat",
      width: "150px",
      render: (row) => (
        <span className="text-content-muted text-xs">{formatDate(row.created_at, true)}</span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Riwayat layanan"
        title="Percakapan"
        description="Seluruh percakapan chatbot dan handoff ke operator."
      />
      <ListError error={error} />
      <Card>
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(row) => row.id}
          isPending={isPending}
          empty={{ icon: <MessagesSquare className="size-5" />, title: "Belum ada percakapan" }}
        />
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Appointments
// --------------------------------------------------------------------------

const appointmentSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        ticket: z.string(),
        patient_name: z.string().nullable().default(null),
        patient_phone: z.string().nullable().default(null),
        appointment_date: z.string().nullable().default(null),
        queue_number: z.string().nullable().default(null),
        status: z.string().nullable().default(null),
        doctor: z.string().nullable().default(null),
        specialty: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

type Appointment = z.infer<typeof appointmentSchema>["items"][number];

const APPOINTMENT_STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export function AdminAppointments() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isPending, error } = useList<Appointment>(
    ["admin", "appointments"],
    "/api/admin/appointments",
    appointmentSchema,
  );

  const update = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.post(`/api/admin/appointments/${id}/status`, okSchema, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "appointments"] });
      toast.success({ title: "Status janji temu diperbarui" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal memperbarui",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  const columns: Column<Appointment>[] = [
    {
      key: "queue",
      header: "Antrean",
      width: "100px",
      render: (row) => (
        <span className="tabular bg-surface-sunken rounded-lg px-2 py-1 text-xs font-bold">
          {row.queue_number ?? "—"}
        </span>
      ),
    },
    {
      key: "patient",
      header: "Pasien",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold">{row.patient_name ?? "—"}</p>
          <p className="text-content-subtle truncate text-xs">{row.patient_phone ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "doctor",
      header: "Dokter",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate">{row.doctor ?? "Belum ditentukan"}</p>
          <p className="text-content-subtle truncate text-xs">{row.specialty ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "date",
      header: "Tanggal",
      width: "140px",
      render: (row) => formatDate(row.appointment_date),
    },
    {
      key: "status",
      header: "Status",
      align: "right",
      width: "170px",
      render: (row) => (
        <select
          value={row.status ?? "pending"}
          onChange={(event) => update.mutate({ id: row.id, status: event.target.value })}
          aria-label={`Status janji temu ${row.ticket}`}
          className="border-hairline-strong bg-surface rounded-lg border px-2 py-1 text-xs font-semibold"
        >
          {APPOINTMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Operasional"
        title="Janji Temu"
        description="Reservasi pasien dan status antreannya."
      />
      <ListError error={error} />
      <Card>
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(row) => row.id}
          isPending={isPending}
          empty={{ icon: <CalendarDays className="size-5" />, title: "Belum ada janji temu" }}
        />
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Doctors
// --------------------------------------------------------------------------

const doctorSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        specialty: z.string().nullable().default(null),
        schedule: z.string().nullable().default(null),
        active: z.boolean(),
      }),
    )
    .default([]),
});

type Doctor = z.infer<typeof doctorSchema>["items"][number];

export function AdminDoctors() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({ name: "", specialty: "", schedule: "" });
  const { data, isPending, error } = useList<Doctor>(
    ["admin", "doctors"],
    "/api/admin/doctors",
    doctorSchema,
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "doctors"] });

  const create = useMutation({
    mutationFn: () => api.post("/api/admin/doctors", okSchema, form),
    onSuccess: () => {
      void invalidate();
      setForm({ name: "", specialty: "", schedule: "" });
      toast.success({ title: "Dokter ditambahkan" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal menambahkan",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  const toggle = useMutation({
    mutationFn: (id: number) => api.post(`/api/admin/doctors/${id}/toggle`, okSchema),
    onSuccess: () => {
      void invalidate();
      toast.success({ title: "Status dokter diperbarui" });
    },
  });

  const columns: Column<Doctor>[] = [
    { key: "name", header: "Nama", render: (row) => <span className="font-semibold">{row.name}</span> },
    { key: "specialty", header: "Poliklinik", render: (row) => row.specialty ?? "—" },
    {
      key: "schedule",
      header: "Jadwal",
      render: (row) => <span className="text-content-muted text-xs">{row.schedule ?? "—"}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "110px",
      render: (row) =>
        row.active ? <Badge tone="routine" dot>Aktif</Badge> : <Badge>Nonaktif</Badge>,
    },
    {
      key: "actions",
      header: "Tindakan",
      align: "right",
      width: "140px",
      render: (row) => (
        <Button
          size="sm"
          icon={<Power className="size-3.5" />}
          loading={toggle.isPending && toggle.variables === row.id}
          onClick={() => toggle.mutate(row.id)}
        >
          {row.active ? "Nonaktifkan" : "Aktifkan"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader eyebrow="Layanan" title="Dokter" description="Daftar dokter dan jadwal praktik." />
      <ListError error={error} />
      <Card className="mb-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
          className="grid gap-3 p-4 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
        >
          <Input
            label="Nama dokter"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
          <Input
            label="Poliklinik"
            value={form.specialty}
            onChange={(event) => setForm({ ...form, specialty: event.target.value })}
          />
          <Input
            label="Jadwal"
            placeholder="Senin, Rabu (08:00 - 12:00)"
            value={form.schedule}
            onChange={(event) => setForm({ ...form, schedule: event.target.value })}
          />
          <Button type="submit" variant="primary" loading={create.isPending}>
            Tambah
          </Button>
        </form>
      </Card>
      <Card>
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(row) => row.id}
          isPending={isPending}
          empty={{ icon: <Stethoscope className="size-5" />, title: "Belum ada dokter" }}
        />
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Knowledge base
// --------------------------------------------------------------------------

const knowledgeSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        tag: z.string(),
        patterns: z.string().nullable().default(null),
        responses: z.string().nullable().default(null),
        updated_at: z.string().nullable().default(null),
        approval_status: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

type Knowledge = z.infer<typeof knowledgeSchema>["items"][number];

export function AdminKnowledge() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isPending, error } = useList<Knowledge>(
    ["admin", "knowledge"],
    "/api/admin/knowledge",
    knowledgeSchema,
  );

  const remove = useMutation({
    mutationFn: (id: number) => api.post(`/api/admin/knowledge/${id}/delete`, okSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "knowledge"] });
      toast.success({ title: "Entri dihapus" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal menghapus",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  const columns: Column<Knowledge>[] = [
    {
      key: "tag",
      header: "Tag",
      width: "160px",
      render: (row) => <Badge tone="accent">{row.tag}</Badge>,
    },
    {
      key: "patterns",
      header: "Pola kata kunci",
      render: (row) => <span className="text-content-muted text-xs">{row.patterns ?? "—"}</span>,
    },
    {
      key: "responses",
      header: "Respons",
      render: (row) => (
        <p className="line-clamp-2 max-w-md text-xs">{row.responses ?? "—"}</p>
      ),
    },
    {
      key: "updated",
      header: "Diperbarui",
      width: "140px",
      render: (row) => (
        <span className="text-content-muted text-xs">{formatDate(row.updated_at)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      width: "70px",
      render: (row) => (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Hapus entri ${row.tag}`}
          loading={remove.isPending && remove.variables === row.id}
          onClick={() => remove.mutate(row.id)}
          icon={<Trash2 className="size-3.5" />}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Chatbot"
        title="Knowledge Base"
        description="Intent dan respons yang dipakai chatbot sebelum jatuh ke model AI."
      />
      <ListError error={error} />
      <Card>
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(row) => row.id}
          isPending={isPending}
          empty={{ icon: <BookOpenText className="size-5" />, title: "Knowledge base kosong" }}
        />
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Users
// --------------------------------------------------------------------------

const userSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        username: z.string(),
        email: z.string().nullable().default(null),
        role: z.string(),
        active: z.boolean(),
      }),
    )
    .default([]),
});

type User = z.infer<typeof userSchema>["items"][number];

export function AdminUsers() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: session } = useSession();
  const currentUsername = isAdmin(session) ? session.username : null;
  const isSuperadmin = isAdmin(session) && session.role === "superadmin";

  const { data, isPending, error } = useList<User>(
    ["admin", "users"],
    "/api/admin/users",
    userSchema,
  );

  const toggle = useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api.post(`/api/admin/users/${id}/toggle`, okSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success({ title: "Status pengguna diperbarui" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal memperbarui",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  const columns: Column<User>[] = [
    {
      key: "name",
      header: "Nama",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold">{row.name}</p>
          <p className="text-content-subtle truncate text-xs">{row.username}</p>
        </div>
      ),
    },
    { key: "email", header: "Email", render: (row) => row.email ?? "—" },
    {
      key: "role",
      header: "Peran",
      width: "140px",
      render: (row) =>
        row.role === "superadmin" ? (
          <Badge tone="accent">Super admin</Badge>
        ) : (
          <Badge>Admin</Badge>
        ),
    },
    {
      key: "status",
      header: "Status",
      width: "110px",
      render: (row) =>
        row.active ? <Badge tone="routine" dot>Aktif</Badge> : <Badge>Nonaktif</Badge>,
    },
    {
      key: "actions",
      header: "Tindakan",
      align: "right",
      width: "150px",
      render: (row) => {
        // The API rejects self-deactivation; the button is disabled to match,
        // so the rule is visible rather than only discovered on failure.
        const isSelf = row.username === currentUsername;
        return (
          <Button
            size="sm"
            icon={<Power className="size-3.5" />}
            disabled={!isSuperadmin || isSelf}
            title={isSelf ? "Tidak dapat menonaktifkan akun sendiri" : undefined}
            loading={toggle.isPending && toggle.variables?.id === row.id}
            onClick={() => toggle.mutate({ id: row.id })}
          >
            {row.active ? "Nonaktifkan" : "Aktifkan"}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Sistem"
        title="Pengguna"
        description="Akun staf yang dapat mengakses panel admin."
      />
      <ListError error={error} />
      {!isSuperadmin && (
        <Alert tone="info" className="mb-5">
          Hanya super admin yang dapat mengubah akun pengguna.
        </Alert>
      )}
      <Card>
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(row) => row.id}
          isPending={isPending}
          empty={{ icon: <UsersRound className="size-5" />, title: "Belum ada pengguna" }}
        />
      </Card>
    </>
  );
}
